import React, { useMemo, useState } from 'react'

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0
}

function useOrigin(): string {
  return useMemo(() => {
    if (typeof window === 'undefined') return ''
    const o = window.location?.origin || ''
    return isNonEmptyString(o) ? o : ''
  }, [])
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

const ReviewerLanding: React.FC = () => {
  const origin = useOrigin()
  const [copyMsg, setCopyMsg] = useState<string>('')

  const endpoints = useMemo(() => {
    const base = origin || ''
    return {
      payloadCreate: `${base}/api/xumm/payload`,
      payloadStatusExample: `${base}/api/xumm/payload/<uuid>`,
      webhook: `${base}/api/xumm/webhook`,
      xapp: `${base}/xapp`,
    }
  }, [origin])

  const doCopy = async (text: string) => {
    setCopyMsg('')
    const ok = await copyToClipboard(text)
    setCopyMsg(ok ? 'Copied to clipboard.' : 'Copy failed (browser blocked clipboard).')
    window.setTimeout(() => setCopyMsg(''), 2200)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(900px 420px at 20% 10%, rgba(99,102,241,0.30), transparent 60%), radial-gradient(700px 420px at 85% 30%, rgba(16,185,129,0.22), transparent 60%), #0b1220',
        color: '#e5e7eb',
        padding: '24px',
      }}
    >
      <div style={{ maxWidth: '980px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#93c5fd' }}>
              Unykorn
            </div>
            <h1 style={{ margin: '8px 0 0', fontSize: '28px', lineHeight: 1.2 }}>Reviewer / Submission</h1>
            <div style={{ marginTop: '6px', color: '#9ca3af', fontSize: '13px', lineHeight: 1.5 }}>
              This page is safe to share publicly. It contains <strong>no secrets</strong>.
            </div>
          </div>

          <a
            href="/"
            style={{
              padding: '10px 12px',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(0,0,0,0.25)',
              color: '#e5e7eb',
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Back to site
          </a>
        </div>

        <div
          style={{
            marginTop: '18px',
            padding: '16px',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Links</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
            <div>
              <strong style={{ color: '#93c5fd' }}>Repo:</strong>{' '}
              <a href="https://github.com/Y3KDigital/xrpl-pof" style={{ color: '#ffffff' }} target="_blank" rel="noreferrer">
                https://github.com/Y3KDigital/xrpl-pof
              </a>
            </div>
            <div>
              <strong style={{ color: '#93c5fd' }}>Reviewer packet:</strong>{' '}
              <a
                href="https://github.com/Y3KDigital/xrpl-pof/blob/main/XRPL-XAMAN-REVIEW-PACKET.md"
                style={{ color: '#ffffff' }}
                target="_blank"
                rel="noreferrer"
              >
                XRPL-XAMAN-REVIEW-PACKET.md
              </a>
            </div>
            <div>
              <strong style={{ color: '#93c5fd' }}>Submission template:</strong>{' '}
              <a
                href="https://github.com/Y3KDigital/xrpl-pof/blob/main/SUBMISSION-TEMPLATE-XRPL-XAMAN.md"
                style={{ color: '#ffffff' }}
                target="_blank"
                rel="noreferrer"
              >
                SUBMISSION-TEMPLATE-XRPL-XAMAN.md
              </a>
            </div>
          </div>

          <div
            style={{
              marginTop: '14px',
              padding: '14px',
              borderRadius: '14px',
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.18)',
              color: '#d1fae5',
              fontSize: '13px',
              lineHeight: 1.6,
            }}
          >
            <strong>Important:</strong> If the API endpoints return “not configured”, set these as <strong>Cloudflare Pages</strong>{' '}
            <strong>Production</strong> environment variables (server-side): <code>XUMM_API_KEY</code> and <code>XUMM_API_SECRET</code>.
            Do <em>not</em> put secrets into client code.
          </div>
        </div>

        <div
          style={{
            marginTop: '18px',
            padding: '16px',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Quick endpoints (this deployment)</div>

          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'monospace' }}>{endpoints.xapp}</code>
              <button
                onClick={() => doCopy(endpoints.xapp)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#e5e7eb',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Copy
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'monospace' }}>{endpoints.payloadCreate}</code>
              <button
                onClick={() => doCopy(endpoints.payloadCreate)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#e5e7eb',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Copy
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'monospace' }}>{endpoints.payloadStatusExample}</code>
              <button
                onClick={() => doCopy(endpoints.payloadStatusExample)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#e5e7eb',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Copy
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'monospace' }}>{endpoints.webhook}</code>
              <button
                onClick={() => doCopy(endpoints.webhook)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#e5e7eb',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Copy
              </button>
            </div>
          </div>

          {copyMsg && (
            <div style={{ marginTop: '10px', color: '#93c5fd', fontSize: '12px' }}>{copyMsg}</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReviewerLanding
